// Role-based access control. Five workspace roles: Owner runs the workspace,
// Admin runs everything but the owner, Manager sees every rep's numbers and may
// export, Sales Rep works their own book, Viewer looks but never touches. The
// current user's role comes from the team roster (matched by email); a
// workspace with no roster yet treats the signed-in user as Owner.
//
// This is client-side gating for UX honesty (hide/disable what you can't do) —
// the backend still authenticates every API call with the caller's own token.
import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react'
import { listMembers } from './team.js'
import { useAuth } from '../auth.jsx'
// ---- Roles ----
// The vocabulary itself lives in roles.js — no React, so code with no UI (the
// morning briefing's "who is the rep?" lookup) can import it without dragging
// auth.jsx in. Re-exported here so every existing rbac.js import still works.
import { ROLE_DEFS, ROLE_KEYS, normalizeRole, roleOrEmpty } from './roles.js'

export { ROLE_DEFS, ROLE_KEYS, normalizeRole, roleOrEmpty }

// ---- Permissions ----
// One key per gated surface. `true` = every role; otherwise the allowed set.
const ALL = ['owner', 'admin', 'manager', 'rep', 'viewer']
const NOT_VIEWER = ['owner', 'admin', 'manager', 'rep']
const LEADERSHIP = ['owner', 'admin', 'manager']
const ADMINS = ['owner', 'admin']

export const PERMISSIONS = {
  // Leads
  'leads.view': ALL,
  'leads.enrich': NOT_VIEWER, // spends OpenAI/Apollo credits
  'leads.import': NOT_VIEWER, // writes rows every teammate's directory shows (the lambda checks the same)
  'leads.removeImport': ADMINS, // deletes an imported region for everyone (the lambda checks the same)
  'leads.export': LEADERSHIP, // raw data leaves the app
  'outcomes.log': NOT_VIEWER,
  'notes.manage': NOT_VIEWER, // working notes on a lead

  // Insight
  'performance.own': NOT_VIEWER,
  'performance.team': LEADERSHIP, // every rep's numbers
  'events.view': ADMINS, // audit log

  // Workspace
  'members.view': ALL,
  'members.invite': LEADERSHIP,
  'members.remove': ADMINS,
  'members.changeRole': ADMINS,
  'integrations.manage': ADMINS, // gmail connect/disconnect etc.

  // Pages. A member (rep) lives in chat: the dashboard and performance
  // surfaces are leadership cockpit, not their workspace — chat is their
  // default view and the sidebar shows only what they work. Viewer keeps the
  // read-only tour of everything.
  'pages.workspace': ['owner', 'admin', 'manager', 'viewer'],
}

export function can(role, permission) {
  const allowed = PERMISSIONS[permission]
  if (!allowed) return true // unknown permission → never brick a page
  return allowed.includes(normalizeRole(role) === role ? role : normalizeRole(role))
}

// ---- Permission grid (checkbox editor) ----
// The same permission keys arranged as modules × actions for the Role &
// Permissions editor: rows are app areas, columns are verbs, each cell is one
// key from PERMISSIONS. Cells a module doesn't have render as "—".
export const GRID_COLUMNS = ['View', 'Manage', 'Enrich', 'Import', 'Export', 'Invite', 'Remove', 'Roles']

export const PERMISSION_GRID = [
  { module: 'Leads', cells: { View: 'leads.view', Enrich: 'leads.enrich', Import: 'leads.import', Export: 'leads.export', Remove: 'leads.removeImport' } },
  { module: 'Deal outcomes', cells: { Manage: 'outcomes.log' } },
  { module: 'Lead notes', cells: { Manage: 'notes.manage' } },
  { module: 'Own performance', cells: { View: 'performance.own' } },
  { module: 'Team performance', cells: { View: 'performance.team' } },
  { module: 'Audit log', cells: { View: 'events.view' } },
  { module: 'Team members', cells: { View: 'members.view', Invite: 'members.invite', Remove: 'members.remove', Roles: 'members.changeRole' } },
  { module: 'Integrations', cells: { Manage: 'integrations.manage' } },
]

export const ALL_PERMISSION_KEYS = Object.keys(PERMISSIONS)

// The full grant set a role preset implies.
export function permissionsForRole(role) {
  const r = normalizeRole(role)
  return new Set(ALL_PERMISSION_KEYS.filter((key) => PERMISSIONS[key].includes(r)))
}

// A member's effective grants: explicit overrides when present (unknown keys
// dropped so stale data can't grant anything), otherwise their role preset.
export function memberPermissions(member) {
  if (Array.isArray(member?.permissions)) {
    return new Set(member.permissions.filter((key) => PERMISSIONS[key]))
  }
  return permissionsForRole(member?.role)
}

export function setsEqual(a, b) {
  return a.size === b.size && [...a].every((k) => b.has(k))
}

// Which preset a grant set matches exactly — or 'custom' when hand-tuned.
export function presetForPermissions(perms) {
  return ROLE_KEYS.find((r) => setsEqual(perms, permissionsForRole(r))) || 'custom'
}

// Roles an actor may assign to others: anything strictly below their own rank
// (so admins can't mint owners/admins-of-equal-rank; owner can assign any).
export function rolesAssignableBy(actorRole) {
  const actor = normalizeRole(actorRole)
  if (actor === 'owner') return ROLE_KEYS
  return ROLE_KEYS.filter((r) => ROLE_DEFS[r].rank < ROLE_DEFS[actor].rank)
}

// ---- Current-user resolution ----
// Matched by email against the roster. Empty roster → Owner (solo workspace);
// on the roster → that row's role + any per-member permission overrides; not on
// a non-empty roster → Owner (the workspace creator isn't in their own invite
// roster).
export function resolveRole(email, members) {
  return resolveAccess(email, members).role
}

export function resolveAccess(email, members) {
  const me = (members || []).find(
    (m) => (m.email || '').toLowerCase() === (email || '').toLowerCase(),
  )
  if (me) {
    return {
      role: normalizeRole(me.role),
      custom: Array.isArray(me.permissions),
      perms: memberPermissions(me),
    }
  }
  return { role: 'owner', custom: false, perms: permissionsForRole('owner') }
}

export const CACHE_KEY = 'revailosales.rbac' // {email, role, custom, perms: [...]}

const RbacContext = createContext(null)

// The access a browser may paint for `email` before the roster answers: their
// OWN cached grants when the cache belongs to them, otherwise the default.
// Carries the email it was resolved for, so a user switch is detectable.
function cachedAccessFor(email) {
  const mine = (email || '').toLowerCase()
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null')
    if (mine && (cached?.email || '').toLowerCase() === mine && cached?.role && Array.isArray(cached.perms)) {
      return {
        email: mine,
        role: normalizeRole(cached.role),
        custom: !!cached.custom,
        perms: new Set(cached.perms.filter((k) => PERMISSIONS[k])),
        // A REAL remembered resolution — a consumer that must not act on the
        // optimistic default (the home redirect) may act on this.
        cached: true,
      }
    }
  } catch {
    /* fall through */
  }
  return { email: mine, role: 'owner', custom: false, perms: permissionsForRole('owner'), cached: false }
}

export function RbacProvider({ children }) {
  const { user } = useAuth()
  // Instant paint from the last-known access — but only the signed-in user's
  // own cache (a shared machine must never paint one user with another's
  // grants) — then refresh from the roster.
  const [access, setAccess] = useState(() => cachedAccessFor(user?.email))
  // Whether `access` is a real resolution (cache or roster) rather than the
  // optimistic owner default painted while the roster is in flight. The home
  // redirect waits on this so a member's very first "/" never bounces through
  // the dashboard's role notice.
  const [ready, setReady] = useState(() => cachedAccessFor(user?.email).cached === true)

  useEffect(() => {
    let cancelled = false
    // This provider sits ABOVE the auth gate, so it does not unmount on
    // sign-out: `access` is whatever the PREVIOUS account resolved to. Reset it
    // the moment the identity changes, before the roster fetch below has
    // answered — otherwise B paints with A's role and grants, and keeps them
    // for the whole session whenever that fetch comes back empty (the
    // `mode === 'local' && !members.length` bail-out just below).
    const mine = (user?.email || '').toLowerCase()
    setAccess((prev) => {
      if (prev.email === mine) return prev
      const fresh = cachedAccessFor(mine)
      setReady(fresh.cached === true)
      return fresh
    })
    if (!user?.email) return undefined
    listMembers().then(({ members, mode }) => {
      if (cancelled) return
      // The roster ANSWERED — even the no-information branch below is an
      // answer for readiness purposes: nothing later in this session will
      // resolve any harder, so consumers may stop waiting.
      setReady(true)
      // In a deployed build a failed roster fetch surfaces as the local
      // fallback with an empty list. That's "no information", not "solo
      // workspace" — don't promote anyone to Owner off an API hiccup.
      if (mode === 'local' && members.length === 0) return
      // Stamped with the account it was resolved for, so the reset above can
      // tell "already resolved for this user" from "left over from the last one".
      const next = { ...resolveAccess(user.email, members), email: mine }
      setAccess(next)
      try {
        localStorage.setItem(
          CACHE_KEY,
          JSON.stringify({
            email: (user.email || '').toLowerCase(),
            role: next.role,
            custom: next.custom,
            perms: [...next.perms],
          }),
        )
        localStorage.removeItem('revailosales.role') // pre-grid cache key
      } catch {
        /* ignore */
      }
    })
    return () => {
      cancelled = true
    }
  }, [user?.email])

  const value = useMemo(
    () => ({
      role: access.role,
      roleLabel: ROLE_DEFS[access.role]?.label || 'Sales Rep',
      custom: access.custom,
      perms: access.perms,
      // False only during the first-ever paint, before cache or roster has
      // answered. Everything except the home redirect ignores it.
      ready,
      can: (permission) =>
        PERMISSIONS[permission] ? access.perms.has(permission) : true, // unknown → never brick a page
    }),
    [access, ready],
  )

  return createElement(RbacContext.Provider, { value }, children)
}

export function useRbac() {
  const ctx = useContext(RbacContext)
  // Pages render outside the provider in tests/storybook — behave as owner.
  return (
    ctx || {
      role: 'owner',
      roleLabel: 'Owner',
      custom: false,
      perms: permissionsForRole('owner'),
      ready: true,
      can: () => true,
    }
  )
}

// ---- Path gating for suggestion surfaces ----
// Chat answers, the assistant panel and search all render little "open this
// page" links. For a role whose app does not HAVE those pages, the link itself
// is the tell — so every suggestion surface filters through this one test.
const WORKSPACE_PATH_RE = /^\/(dashboard|performance|logs)(\/|$)/
export function pathAllowedFor(can, to) {
  return !WORKSPACE_PATH_RE.test(String(to || '')) || can('pages.workspace')
}
